import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { wallTierFaps } from "@gooner/shared";
import { usePublicSettings } from "../lib/publicSettings";
import { trackFapsWallClicked, trackFapsWelcomeShown, useFapsStore } from "../lib/faps";
import { FapsAmount } from "../control/ui/FapsAmount";
import { IconClose } from "../control/ui/icons";

/**
 * The welcome — one card, once, the moment the welcome grant lands.
 *
 * The grant is LAZY (the first wallet read, not sign-up), so the moment arrives on
 * whatever page the person first opens with Faps on. The store latches the wallet's
 * one-time `welcomed` flag into `welcomePending`; this renders while it is set and clears
 * it on dismiss. Nothing is persisted here: the server never flags a second read, so a tab
 * closed before dismissing simply never greets — a miss, not a repeat.
 *
 * COPY, deliberately not "you've got 100": the grant is lazy, so an account older than
 * the switch or a member whose stipend already landed reads this with a balance that is
 * not the grant. It names the GRANT and what a square costs, both from settings, and it
 * says nothing about money (the Faps copy rules). The crown beside a number IS the amount.
 *
 * A portal fixed under the bar rather than a child of the pill: the pill is a bare button
 * in the bar's flex row, and wrapping it would reflow the bar on both hosts. The CTA is
 * the same-tab route to the wall the How-To links; it hides when the wall is already what
 * is on screen.
 */
export function FapsWelcomeCard() {
  const pending = useFapsStore((s) => s.welcomePending);
  const dismiss = useFapsStore((s) => s.dismissWelcome);
  const balance = useFapsStore((s) => s.wallet?.balance ?? null);
  const settings = usePublicSettings();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (pending) trackFapsWelcomeShown();
  }, [pending]);

  if (!pending || !settings?.faps?.enabled) return null;
  const grant = Math.round(settings.faps.welcomeGrant);
  const small = wallTierFaps(settings.faps, "small");
  const onTheWall = pathname === "/screen" || pathname === "/";

  return createPortal(
    <div
      role="status"
      className="fixed right-3 top-14 z-[55] w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border border-[#f5c518]/40 bg-[var(--color-panel)] p-4 shadow-2xl"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded p-1 text-zinc-500 hover:bg-white/10 hover:text-white"
      >
        <IconClose width={14} height={14} />
      </button>
      <p className="pr-6 text-sm text-zinc-100">
        Here&rsquo;s <FapsAmount n={grant} size={13} /> to get you started.
      </p>
      {small !== null ? (
        <p className="mt-1 text-xs text-zinc-400">
          Squares on the wall start at <FapsAmount n={small} size={11} gold={false} />.
        </p>
      ) : null}
      {!onTheWall ? (
        <button
          type="button"
          onClick={() => {
            trackFapsWallClicked("welcome", small !== null && (balance ?? 0) < small);
            dismiss();
            navigate("/screen?code=0");
          }}
          className="mt-3 rounded-lg bg-[#f5c518] px-3 py-1.5 text-sm font-semibold text-black hover:brightness-110"
        >
          See the wall
        </button>
      ) : null}
    </div>,
    document.body,
  );
}
